/**
 * Group Sender Key Manager
 * Manages sender keys for group conversations
 */

import { SenderKey, generateSenderKey, serializeSenderKey, deserializeSenderKey, decryptSenderKeyFromBlob } from './senderKeys';
import nacl from 'tweetnacl';
import * as naclUtil from 'tweetnacl-util';

export interface GroupSenderKey {
  groupId: string;
  senderKey: SenderKey;
  encryptedForMembers: Record<string, string>; // userId -> encrypted sender key
  createdAt: number;
  lastRotated: number;
}

/**
 * Group Sender Key Manager Class
 */
export class GroupSenderKeyManager {
  private static readonly GROUP_KEY_STORAGE_PREFIX = 'group_sender_key_';
  private static readonly KEY_ROTATION_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days
  private static readonly MAX_MESSAGES_PER_KEY = 1000; // Rotate after 1000 messages
  
  /**
   * Get or create sender key for a group
   */
  static getOrCreateSenderKey(
    groupId: string,
    memberIds: string[],
    senderId: string,
    senderSecretKey: Uint8Array
  ): GroupSenderKey {
    // Try to get existing key
    const existing = this.getSenderKey(groupId, senderId);
    
    // Check if key needs rotation
    const needsRotation = existing && (
      Date.now() - existing.lastRotated > this.KEY_ROTATION_INTERVAL ||
      existing.senderKey.messageCount >= this.MAX_MESSAGES_PER_KEY
    );
    
    if (existing && !needsRotation) {
      return existing;
    }
    
    // Generate new sender key
    const senderKey = generateSenderKey();
    
    // Encrypt sender key for all members
    const encryptedForMembers: Record<string, string> = {};
    
    // Get public keys for all members
    memberIds.forEach(memberId => {
      // In real implementation, fetch from database
      // For now, we'll need to pass public keys
      // This is a placeholder - actual implementation will fetch from DB
    });
    
    const groupKey: GroupSenderKey = {
      groupId,
      senderKey,
      encryptedForMembers,
      createdAt: Date.now(),
      lastRotated: Date.now()
    };
    
    this.storeSenderKey(groupId, senderId, groupKey);
    
    return groupKey;
  }
  
  /**
   * Get sender key for a group
   */
  static getSenderKey(groupId: string, senderId: string): GroupSenderKey | null {
    const key = `${this.GROUP_KEY_STORAGE_PREFIX}${groupId}_${senderId}`;
    const serialized = localStorage.getItem(key);
    
    if (!serialized) {
      return null;
    }
    
    try {
      const data = JSON.parse(serialized);
      return {
        groupId: data.groupId,
        senderKey: deserializeSenderKey(data.senderKey),
        encryptedForMembers: data.encryptedForMembers,
        createdAt: data.createdAt,
        lastRotated: data.lastRotated
      };
    } catch (error) {
      console.error('Error deserializing group sender key:', error);
      return null;
    }
  }
  
  /**
   * Store sender key
   */
  static storeSenderKey(groupId: string, senderId: string, groupKey: GroupSenderKey): void {
    const key = `${this.GROUP_KEY_STORAGE_PREFIX}${groupId}_${senderId}`;
    const serialized = JSON.stringify({
      groupId: groupKey.groupId,
      senderKey: serializeSenderKey(groupKey.senderKey),
      encryptedForMembers: groupKey.encryptedForMembers,
      createdAt: groupKey.createdAt,
      lastRotated: groupKey.lastRotated
    });
    
    localStorage.setItem(key, serialized);
  }
  
  /**
   * Update sender key (after encrypting message)
   */
  static updateSenderKey(groupId: string, senderId: string, updatedSenderKey: SenderKey): void {
    const groupKey = this.getSenderKey(groupId, senderId);
    if (!groupKey) {
      throw new Error('Group sender key not found');
    }
    
    groupKey.senderKey = updatedSenderKey;
    this.storeSenderKey(groupId, senderId, groupKey);
  }
  
  /**
   * Get sender key for a recipient (decrypted)
   */
  static async getSenderKeyForRecipient(
    groupId: string,
    senderId: string,
    recipientId: string,
    recipientSecretKey: Uint8Array,
    senderPublicKey: Uint8Array
  ): Promise<SenderKey | null> {
    const groupKey = this.getSenderKey(groupId, senderId);
    if (!groupKey) {
      return null;
    }
    
    const encryptedBlob = groupKey.encryptedForMembers[recipientId];
    if (!encryptedBlob) {
      return null;
    }
    
    // Decrypt sender key
    try {
      return decryptSenderKeyFromBlob(
        encryptedBlob,
        recipientSecretKey,
        senderPublicKey
      );
    } catch (error) {
      console.error('Error decrypting sender key:', error);
      return null;
    }
  }
  
  /**
   * Rotate sender key for a group
   */
  static rotateSenderKey(
    groupId: string,
    senderId: string,
    memberIds: string[],
    senderSecretKey: Uint8Array
  ): GroupSenderKey {
    // Delete old key
    this.deleteSenderKey(groupId, senderId);
    
    // Create new key
    return this.getOrCreateSenderKey(groupId, memberIds, senderId, senderSecretKey);
  }
  
  /**
   * Delete sender key
   */
  static deleteSenderKey(groupId: string, senderId: string): void {
    const key = `${this.GROUP_KEY_STORAGE_PREFIX}${groupId}_${senderId}`;
    localStorage.removeItem(key);
  }
  
  /**
   * Clear all sender keys for a group
   */
  static clearGroupKeys(groupId: string): void {
    const prefix = `${this.GROUP_KEY_STORAGE_PREFIX}${groupId}_`;
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
}

